import type { FC, ReactNode } from 'react'

import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/landing/reveal'
import MemoryPhoto from '@/components/landing/memory-photo'
import EqualizerBars from '@/components/landing/equalizer-bars'
import SectionHeading from '@/components/landing/section-heading'
import { useSong } from '@/components/landing/song-provider'
import { PHOTOS, SONG } from '@/constants/birthday'

const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const minutes = Math.floor(seconds / 60)
    const rest = Math.floor(seconds % 60)

    return `${minutes}:${String(rest).padStart(2, '0')}`
}

const SongSection: FC = (): ReactNode => {
    const {
        playing,
        current,
        duration,
        failed,
        toggle,
        seek
    } = useSong()

    return (
        <section id='song' aria-labelledby='song-title' className='relative overflow-hidden py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        id='song-title'
                        title={SONG.title}
                        subtitle={SONG.subtitle}
                    />
                </Reveal>

                <Reveal className='mt-14'>
                    <div className='relative mx-auto grid max-w-4xl items-center gap-10 overflow-hidden rounded-[2.5rem] bg-wine p-6 text-white shadow-2xl shadow-wine/30 sm:p-10 md:grid-cols-[auto_1fr] md:gap-12'>
                        <div aria-hidden className='pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-primary/30 blur-3xl' />

                        <div className='relative mx-auto size-56 sm:size-64'>
                            <div
                                className={cn(
                                    'vinyl-grooves absolute inset-0 rounded-full shadow-2xl shadow-black/50 ring-1 ring-white/10 animate-[spin_9s_linear_infinite] motion-reduce:animate-none',
                                    !playing && '[animation-play-state:paused]'
                                )}
                            >
                                <div className='absolute inset-[29%] overflow-hidden rounded-full ring-4 ring-wine'>
                                    <MemoryPhoto photo={PHOTOS.photo7} ratio={1} className='h-full' />
                                </div>

                                <span className='absolute left-1/2 top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-wine ring-2 ring-white/40' />
                            </div>

                            <span
                                aria-hidden
                                className={cn(
                                    'absolute -right-3 -top-2 h-32 w-1.5 origin-top rounded-full bg-gradient-to-b from-white to-white/60 shadow-lg transition-transform duration-700 motion-reduce:transition-none',
                                    playing ? 'rotate-[26deg]' : 'rotate-[8deg]'
                                )}
                            />
                        </div>

                        <div className='relative text-center md:text-left'>
                            <div className='flex items-center justify-center gap-3 text-rose-300 md:justify-start'>
                                <EqualizerBars playing={playing} />
                                <span className='text-xs font-extrabold uppercase tracking-[0.25em]'>
                                    {SONG.title}
                                </span>
                            </div>

                            <h3 className='mt-3 font-heading text-4xl font-semibold sm:text-5xl'>
                                {SONG.name}
                            </h3>

                            <p className='mt-1 text-white/70'>
                                {SONG.artist}
                            </p>

                            <div className='mt-6'>
                                <input
                                    type='range'
                                    min={0}
                                    max={duration || 0}
                                    step={0.1}
                                    value={Math.min(current, duration || 0)}
                                    onChange={(event) => seek(Number(event.target.value))}
                                    disabled={!duration}
                                    aria-label={SONG.seek}
                                    className='h-1.5 w-full cursor-pointer accent-rose-400 disabled:cursor-default'
                                />

                                <div className='mt-2 flex justify-between text-xs font-semibold tabular-nums text-white/60'>
                                    <span>{formatTime(current)}</span>
                                    <span>{formatTime(duration)}</span>
                                </div>
                            </div>

                            <button
                                type='button'
                                onClick={toggle}
                                className='mt-6 inline-flex items-center gap-2.5 rounded-full bg-primary px-7 py-3.5 font-bold text-primary-foreground shadow-lg shadow-black/25 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40'
                            >
                                {playing ? <Pause className='size-5 fill-current' /> : <Play className='size-5 fill-current' />}
                                {playing ? SONG.pause : SONG.play}
                            </button>

                            {failed && (
                                <p role='alert' className='mt-3 text-sm font-semibold text-rose-200'>
                                    {SONG.error}
                                </p>
                            )}

                            <blockquote className='mt-8 border-l-2 border-rose-300/50 pl-5 text-left font-hand text-2xl leading-snug text-white/90 sm:text-[1.65rem]'>
                                {SONG.note}
                            </blockquote>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default SongSection
